import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { BehaviorSubject, catchError, Observable, switchMap, tap, throwError } from "rxjs";
import { isBefore } from 'date-fns';
import { API_BASE_URL } from "../tokens/api.tokens";
import { ErrorHandlerService } from "./error-handler.service";
import { NotificationService } from "./notification.service";
import { jwtDecode } from "jwt-decode";
import { User } from "../../modules/admin/models/user.model";
import { UserService } from "../../modules/admin/services/user.service";

interface DecodedToken {
  userId: number; 
  roles: string[]; 
  exp: number;
  iat: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl : string;  
  private userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable(); 
    
  constructor(
    private http: HttpClient, 
    @Inject(API_BASE_URL) apiBaseUrl: string, 
    private notification: NotificationService, 
    private errorHandler: ErrorHandlerService,
    private userService: UserService
  ) { 
    this.baseUrl = `${apiBaseUrl}auth/`;
    this.loadUserFromToken();
  }  
    
  login(email: string, password: string) {
    return this.http.post<{ idToken: string; expiresIn: string }>(`${this.baseUrl}login/`, { email, password }).pipe(
      catchError(error => this.catchError(error)), 
      tap(authResult => this.setSession(authResult)),
      switchMap(() => this.fetchUser()), 
    );
  }

  private setSession(authResult: { idToken: string; expiresIn: string }) {
    localStorage.setItem('id_token', authResult.idToken);      
  }

  logout() {
    localStorage.removeItem("id_token");    
    this.userSubject.next(null); // Clear the user data    
  }

  isLoggedIn() {
    const token = localStorage.getItem("id_token");
    return token !== null && 
      token !== undefined && 
      token !== '' && 
      !this.isTokenExpired(token);       
   }

  isLoggedOut(): boolean {
      return !this.isLoggedIn();
  }

  getRoles(): string[] {
    const token = localStorage.getItem("id_token");
    if (token) {
      const decodedToken = jwtDecode<DecodedToken>(token);
      return decodedToken.roles; // Return the roles from the decoded token
    }
    return []; // Return an empty array if no token is present
  }

  hasRole(role: string): boolean {
    const roles = this.getRoles();
    return roles.includes(role); // Check if the user's roles include the specified role
  }

  hasRoles(allowedRoles: string[]): boolean {      
    return allowedRoles.some(role => this.hasRole(role) ); // Check if any of the user's roles match the allowed roles; // Check if any of the user's roles match the allowed roles
  }
  
  private fetchUser(): Observable<User> {
    return this.userService.getMe().pipe(
      tap(user => this.userSubject.next(user)),
      catchError(err => {
        this.logout();
        this.notification.error("Login failed");
        return throwError(() => err);
      })
    );
  }

  private loadUserFromToken(): void {
    const token = localStorage.getItem("id_token");
    if (token && !this.isTokenExpired(token)) {
      this.fetchUser().subscribe({
        next: () => {}
      });
    }
  }

  private catchError(error: HttpErrorResponse) {
      if(error.status === 401) {
          this.notification.error("Invalid email or password.");
          return throwError(() => error)            
      }
      else {
        return this.errorHandler.handle(error);
      }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decodedToken = jwtDecode<DecodedToken>(token);
      const expirationDate = new Date(decodedToken.exp * 1000); // Convert to milliseconds
      return isBefore(expirationDate, new Date()); // Check if the expiration date is before the current date
    }
    catch (error) {
      console.error("Error decoding token:", error);
      return true; // If there's an error decoding the token, consider it expired
    }
  }
  
}